import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { toysApi, ordersApi } from "@/lib/api";
import CertificatePreview from "@/components/CertificatePreview";
import { ArrowLeft, Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";

const initialForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  addr1: "",
  addr2: "",
  city: "",
  postal: "",
  country: "",
  isGift: false,
  giftRecipient: "",
  message: "",
};

const Adopt = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [toy, setToy] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    toysApi.get(id).then(setToy).catch(() => setToy(null));
  }, [id]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["firstName", "lastName", "email", "addr1", "city", "postal", "country"];
    for (const k of required) {
      if (!form[k]?.trim()) {
        toast.error(t.adopt.requiredError);
        return;
      }
    }
    setSubmitting(true);
    try {
      const order = await ordersApi.create({
        toy_id: id,
        buyer_first_name: form.firstName,
        buyer_last_name: form.lastName,
        buyer_email: form.email,
        buyer_phone: form.phone,
        address_line1: form.addr1,
        address_line2: form.addr2,
        city: form.city,
        postal_code: form.postal,
        country: form.country,
        is_gift: form.isGift,
        gift_recipient: form.isGift ? form.giftRecipient : "",
        adoption_message: form.isGift ? form.message : "",
        language: lang,
      });
      toast.success(t.adopt.success);
      navigate(`/confirmation/${order.id}`);
    } catch (err) {
      console.error(err);
      toast.error(t.adopt.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!toy) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <p className="font-body text-muted-foreground">{t.common.loading}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 md:py-16" data-testid="adopt-page">
      <Link
        to={`/toy/${toy.id}`}
        className="inline-flex items-center gap-2 text-sm text-ink/70 hover:text-terracotta transition-colors mb-10"
      >
        <ArrowLeft size={14} strokeWidth={1.5} />
        {t.common.backToCatalog}
      </Link>

      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
        {/* FORM */}
        <div>
          <p className="font-script text-camel text-2xl">{t.adopt.subtitle}</p>
          <h1 className="font-serif-display text-4xl md:text-5xl text-ink mt-1">{t.adopt.title}</h1>

          <form onSubmit={submit} className="mt-10 space-y-8" data-testid="adopt-form">
            <Section title={t.adopt.buyerInfo}>
              <Row>
                <Field label={t.adopt.firstName} required value={form.firstName} onChange={update("firstName")} testid="input-firstName" />
                <Field label={t.adopt.lastName} required value={form.lastName} onChange={update("lastName")} testid="input-lastName" />
              </Row>
              <Row>
                <Field type="email" label={t.adopt.email} required value={form.email} onChange={update("email")} testid="input-email" />
                <Field label={t.adopt.phone} value={form.phone} onChange={update("phone")} testid="input-phone" />
              </Row>
            </Section>

            <Section title={t.adopt.address}>
              <Field label={t.adopt.addr1} required value={form.addr1} onChange={update("addr1")} testid="input-addr1" />
              <Field label={t.adopt.addr2} value={form.addr2} onChange={update("addr2")} testid="input-addr2" />
              <Row>
                <Field label={t.adopt.city} required value={form.city} onChange={update("city")} testid="input-city" />
                <Field label={t.adopt.postal} required value={form.postal} onChange={update("postal")} testid="input-postal" />
              </Row>
              <Field label={t.adopt.country} required value={form.country} onChange={update("country")} testid="input-country" />
            </Section>

            <Section title={t.adopt.giftSection}>
              <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="gift-checkbox-label">
                <input
                  type="checkbox"
                  checked={form.isGift}
                  onChange={(e) => setForm((f) => ({ ...f, isGift: e.target.checked }))}
                  data-testid="input-isGift"
                  className="w-4 h-4 accent-terracotta"
                />
                <span className="font-body text-sm text-ink">{t.adopt.isGift}</span>
              </label>
              <Field
                label={t.adopt.giftRecipient}
                value={form.giftRecipient}
                onChange={update("giftRecipient")}
                disabled={!form.isGift}
                placeholder={form.isGift ? "" : ""}
                testid="input-giftRecipient"
              />
              {form.isGift && (
                <p className="text-xs text-muted-foreground -mt-2">{t.adopt.giftRecipientHelp}</p>
              )}
            </Section>

            <Section title={t.adopt.message}>
              <Field
                as="textarea"
                rows={3}
                maxLength={200}
                placeholder={t.adopt.messageHelp}
                value={form.message}
                onChange={update("message")}
                disabled={!form.isGift}
                testid="input-message"
              />
              <p className="text-xs text-muted-foreground -mt-2">{form.message.length}/200</p>
            </Section>

            <div className="border-t border-border pt-6 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{t.adopt.total}</div>
                <div className="font-serif-display text-3xl text-terracotta">
                  {t.common.currency}{toy.price} <span className="text-lg text-camel">{t.common.currencyCode}</span>
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-adoption"
                className="inline-flex items-center gap-3 bg-terracotta text-background px-7 py-3.5 rounded-full font-body text-sm tracking-wide hover:shadow-warm transition-all disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> {t.adopt.processing}
                  </>
                ) : (
                  <>
                    <Heart size={16} fill="currentColor" /> {t.adopt.submit}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* CERTIFICATE PREVIEW */}
        <div className="lg:sticky lg:top-28 self-start">
          <p className="font-script text-camel text-2xl mb-3">{t.adopt.certificatePreview}</p>
          <CertificatePreview
            toy={toy}
            buyer={{ ...form, isGift: form.isGift, giftRecipient: form.giftRecipient }}
            message={form.isGift ? form.message : ""}
            certificateNumber="CERT-PREVIEW"
          />
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="space-y-4">
    <h3 className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{title}</h3>
    <div className="space-y-4">{children}</div>
  </div>
);

const Row = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;

const Field = ({ as = "input", label, required, value, onChange, type = "text", testid, disabled, ...rest }) => {
  const Tag = as;
  return (
    <label className={`block ${disabled ? "opacity-60" : ""}`}>
      {label && (
        <span className="block text-xs text-ink/70 mb-1.5 font-body">
          {label} {required && <span className="text-terracotta">*</span>}
        </span>
      )}
      <Tag
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        className={`w-full bg-cream/40 border border-border rounded-lg px-4 py-3 font-body text-sm text-ink outline-none focus:border-terracotta focus:bg-cream/70 transition-colors ${
          disabled ? "cursor-not-allowed bg-muted/30" : ""
        }`}
        {...rest}
      />
    </label>
  );
};

export default Adopt;
